import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Button, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';

const { Title } = Typography;
const { Search } = Input;

const PortfolioSelectorV2 = ({ selectedIds = [], onSelectionChange }) => {
  const [selectedPortfolios, setSelectedPortfolios] = useState([]);
  const [availablePortfolios, setAvailablePortfolios] = useState([]);
  const [searchSelected, setSearchSelected] = useState('');
  const [searchAvailable, setSearchAvailable] = useState('');
  const portfolios = useSelector(state => state.portfolios.items);

  useEffect(() => {
    const selected = portfolios.filter(p => selectedIds.includes(p.id));
    const available = portfolios.filter(p => !selectedIds.includes(p.id));
    setSelectedPortfolios(selected);
    setAvailablePortfolios(available);
  }, [selectedIds, portfolios]);

  const addToSelected = (portfolio) => {
    const newSelected = [...selectedPortfolios, portfolio];
    const newAvailable = availablePortfolios.filter(p => p.id !== portfolio.id);
    setSelectedPortfolios(newSelected);
    setAvailablePortfolios(newAvailable);
    onSelectionChange(newSelected.map(p => ({ id: p.id, type: p.type })));
  };

  const removeFromSelected = (portfolio) => {
    const newSelected = selectedPortfolios.filter(p => p.id !== portfolio.id);
    const newAvailable = [...availablePortfolios, portfolio];
    setSelectedPortfolios(newSelected);
    setAvailablePortfolios(newAvailable);
    onSelectionChange(newSelected.map(p => ({ id: p.id, type: p.type })));
  };

  const debouncedSearchSelected = useCallback(debounce((value) => {
    setSearchSelected(value);
  }, 300), []);

  const debouncedSearchAvailable = useCallback(debounce((value) => {
    setSearchAvailable(value);
  }, 300), []);

  const handleSearchSelected = (event) => {
    debouncedSearchSelected(event.target.value);
  };

  const handleSearchAvailable = (event) => {
    debouncedSearchAvailable(event.target.value);
  };

  const filteredSelected = selectedPortfolios.filter(p => p.name.toLowerCase().includes(searchSelected.toLowerCase()));
  const filteredAvailable = searchAvailable === ''
    ? availablePortfolios
    : availablePortfolios.filter(p => p.name.toLowerCase().includes(searchAvailable.toLowerCase()));

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Type', dataIndex: 'type', key: 'type' },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Button onClick={() => record.isSelected ? removeFromSelected(record) : addToSelected(record)}>
          {record.isSelected ? 'Remove' : 'Add'}
        </Button>
      )
    }
  ];

  return (
    <div>
      <Title level={4}>Selected Portfolios ({selectedPortfolios.length})</Title>
      <Search
        placeholder="Search selected portfolios"
        onChange={handleSearchSelected}
        style={{ marginBottom: 10 }}
      />
      <Table
        dataSource={filteredSelected.map(p => ({ ...p, isSelected: true }))}
        columns={columns}
        rowKey="id"
        pagination={false}
        scroll={{ y: 180 }}
      />
      <Title level={4} style={{ marginTop: 20 }}>Available Portfolios</Title>
      <Search
        placeholder="Search available portfolios"
        onChange={handleSearchAvailable}
        style={{ marginBottom: 10 }}
      />
      <Table
        dataSource={filteredAvailable.map(p => ({ ...p, isSelected: false }))}
        columns={columns}
        rowKey="id"
        pagination={false}
        scroll={{ y: 180 }}
      />
    </div>
  );
};

export default PortfolioSelectorV2;
